import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from '../../context';
import { useApi } from '../../hooks/useApi';
import {
    Card,
    CardBody,
    CardHeader,
    Dialog, DialogBody,
    Typography,
    IconButton,
    CardFooter,
    DialogHeader,
    Button,
    DialogFooter
} from "@material-tailwind/react";
import { ClockLoader } from 'react-spinners';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IoIosAddCircleOutline, IoMdRemoveCircleOutline } from 'react-icons/io';

export default function EditBooking() {
    let { guestMobile, bookingId, bookingStatus } = useParams();
    const { fetchAccessToken, defaultHotel, isAdmin, assignments } = useUserContext();
    const { getBookingsById, updateBooking, uploadDocument, getImageUrl, deleteBookingsById } = useApi();
    const [roomCategories, setRoomCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [attachment, setAttachment] = useState(null);
    const [useCamera, setUseCamera] = useState(false);
    const [isShowProofImage, setShowProofImage] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [initialValues, setInitialValues] = useState({
        bookingId: 0,
        guestMobile: "",
        guestName: "",
        bookingDate: getTomorrowDate(),
        bookingTime: "09:00",
        bookingDays: 1,
        advancePayment: 0,
        paymentDate: getTodayDate(),
        paymentMode: "",
        documentS3Ids: [],
        notes: "",
        roomBookings: [],
        createdOn: 0,
        bookingStatus: "CONFIRMED"
    });

    useEffect(() => {

        const getBookingInternal = async () => {
            setIsLoading(true);
            try {
                const accessToken = await fetchAccessToken();
                const bookingData = await getBookingsById(defaultHotel, guestMobile, bookingId, bookingStatus, accessToken);
                if (bookingData) {
                    const [datePart, timePart] = bookingData.checkInDateTime.split(" ");
                    setInitialValues({
                        bookingId: bookingData.bookingId,
                        guestMobile: bookingData.guestMobile,
                        guestName: bookingData.guestName,
                        bookingDate: datePart,
                        bookingTime: timePart,
                        bookingDays: bookingData.numberOfDays,
                        advancePayment: bookingData.advancePayment,
                        paymentDate: bookingData.paymentDate || getTodayDate(),
                        paymentMode: bookingData.paymentMode,
                        documentS3Ids: bookingData.documentS3Ids,
                        notes: bookingData.notes,
                        roomBookings: bookingData.roomBookings,
                        createdOn: bookingData.createdOn,
                        bookingStatus: bookingData.bookingStatus
                    });
                }
            } catch (error) {
                console.error('Failed to fetch booking:', error);
            } finally {
                setIsLoading(false);
            }
        };



        if (defaultHotel && guestMobile && bookingId && bookingStatus) {
            getBookingInternal();
        }
    }, [defaultHotel]);

    const bookingFormik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            guestMobile: Yup.string().matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits').required('Guest mobile is required'),
            guestName: Yup.string().required('Guest name is required'),
            bookingDate: Yup.date().required('Booking date is required').min(new Date(new Date().setHours(0, 0, 0, 0)), 'Booking date must be in the today or future'),
            bookingTime: Yup.string().required('Booking time is required'),
            bookingDays: Yup.number().required('No. of Days is required').positive('No. of Days must be a positive number'),
            roomBookings: Yup.array().of(
                Yup.object().shape({
                    category: Yup.string().required('Room Category is required'),
                    numberOfRooms: Yup.number().required('No. of Rooms is required').positive('No. of Rooms must be a positive number'),
                    pricePerRoom: Yup.number().positive('Price per room must be a positive number'),
                    discount: Yup.number().required("Discount field is required").min(0, 'Discount must be a positive number'),
                })
            ).min(1, 'Room Details cannot be empty')
        }),
        onSubmit: async (values, actions) => {
            if (!defaultHotel) {
                return;
            }
            if (values.advancePayment > 0 && values.paymentMode === "") {
                alert("Please select payment mode");
                return;
            }
            try {
                setIsLoading(true);
                const accessToken = await fetchAccessToken();
                if (attachment) {
                    let base64String = await convertBlobToBase64(attachment);
                    let documentS3Id = await uploadDocument(base64String, accessToken);
                    values.documentS3Ids.push(documentS3Id);
                }

                let bookingEndDate = addDaysToDate(values.bookingDate, values.bookingDays);
                let booingEndTime = subtractHourFromTime(values.bookingTime);
                let checkInEpoch = getEpoch(values.bookingDate, values.bookingTime);
                let checkOutEpoch = getEpoch(bookingEndDate, booingEndTime);
                const advanceBooking = {
                    hotelId: defaultHotel,
                    bookingId: values.bookingId,
                    checkInEpoch: checkInEpoch,
                    checkOutEpoch: checkOutEpoch,
                    checkInDateTime: `${values.bookingDate} ${values.bookingTime}`,
                    checkOutDateTime: `${bookingEndDate} ${booingEndTime}`,
                    numberOfDays: values.bookingDays,
                    guestName: values.guestName,
                    guestMobile: values.guestMobile,
                    advancePayment: values.advancePayment,
                    pendingAmount: totalCost - totalDiscount - values.advancePayment,
                    paymentDate: values.paymentDate,
                    paymentMode: values.paymentMode,
                    documentS3Ids: values.documentS3Ids,
                    roomBookings: values.roomBookings,
                    bookingStatus: values.bookingStatus,
                    notes: values.notes,
                    redeemDetails: [],
                    createdOn: values.createdOn,
                    updatedOn: Date.now()
                };
                await updateBooking(defaultHotel, advanceBooking, accessToken);
                alert("Booking updated successfully");
                navigate("/manager/bookings");
            } catch (error) {
                alert("Error in updating booking");
            }
            finally {
                setIsLoading(false);
            }
        }
    });

    function addDaysToDate(bookingDate, bookingDays) {
        const date = new Date(bookingDate);
        const daysToAdd = parseInt(bookingDays, 10);
        date.setDate(date.getDate() + daysToAdd);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    function subtractHourFromTime(bookingTime) {
        const [hours, minutes] = bookingTime.split(':').map(Number);

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);

        date.setHours(date.getHours() - 1);

        const newHours = String(date.getHours()).padStart(2, '0');
        const newMinutes = String(date.getMinutes()).padStart(2, '0');

        return `${newHours}:${newMinutes}`;
    }

    function getEpoch(date, time) {
        const [hours, minutes] = time.split(':').map(Number);
        const now = new Date();
        const seconds = now.getSeconds();
        const milliseconds = now.getMilliseconds();
        const combinedDate = new Date(date);
        combinedDate.setHours(hours);
        combinedDate.setMinutes(minutes);
        combinedDate.setSeconds(seconds);
        combinedDate.setMilliseconds(milliseconds);

        return combinedDate.getTime();
    }

    function getTomorrowDate() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const date = String(now.getDate() + 1).padStart(2, '0');
        return `${year}-${month}-${date}`;
    }

    function getTodayDate() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const date = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${date}`;
    }

    const handleFileChange = (e) => {
        let file = e.target.files[0];
        if (file && (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png')) {
            compressImage(file, (compressedBlob) => {
                setAttachment(compressedBlob);

            });
        } else {
            alert('Please upload a JPEG or PNG image.');
        }
    };

    const compressImage = (file, callback) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const fileType = file.type;
                const isJPEG = fileType === 'image/jpeg' || fileType === 'image/jpg';

                let quality = isJPEG ? 1.0 : undefined;
                const maxFileSize = 1000000; // 1MB

                const compress = () => {
                    canvas.toBlob((blob) => {
                        if (blob.size <= maxFileSize || (isJPEG && quality < 0.1)) {
                            callback(blob);
                        } else if (isJPEG) {
                            quality -= 0.1;
                            compress();
                        } else {
                            // For PNG, if the first compression is too large, we can't reduce quality, so we just use the first result
                            callback(blob);
                        }
                    }, fileType, quality);
                };

                compress();
            };
        };
        reader.readAsDataURL(file);
    };

    function convertBlobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    useEffect(() => {
        if (defaultHotel) {
            let hotel = assignments?.hotels.find(hotel => hotel.id.toLowerCase() === defaultHotel.toLowerCase());
            setRoomCategories(hotel.roomTypes);
        }
    }, [defaultHotel]);

    const totalCost = useMemo(() => {
        return bookingFormik.values.roomBookings.reduce((total, booking) => {
            return total + (booking.pricePerRoom * booking.numberOfRooms * bookingFormik.values.bookingDays);
        }, 0);
    }, [bookingFormik.values.roomBookings, bookingFormik.values.bookingDays]);

    const totalDiscount = useMemo(() => {
        return bookingFormik.values.roomBookings.reduce((total, booking) => {
            return total + (booking.discount * booking.numberOfRooms * bookingFormik.values.bookingDays);
        }, 0);
    }, [bookingFormik.values.roomBookings, bookingFormik.values.bookingDays]);

    let handleSubmitInternal = () => {
        bookingFormik.handleSubmit();
    };

    let deleteBookingInternal = async () => {
        if (!defaultHotel || !guestMobile || !bookingId || !bookingStatus) {
            return;
        }
        const confirm = window.confirm("Do you want to cancel booking?");
        if(!confirm){
            return;
        }
        setIsLoading(true);
        try {
            const accessToken = await fetchAccessToken();
            await deleteBookingsById(defaultHotel, guestMobile, bookingId, bookingStatus, accessToken);
            alert("Booking cancelled successfully");
            navigate("/manager/bookings");
        } catch (error) {
            alert("Error in cancelling booking");
        } finally {
            setIsLoading(false);
        }
    };

    let handleAddRoom = () => {
        bookingFormik.setFieldValue("roomBookings", [...bookingFormik.values.roomBookings, {
            category: "",
            numberOfRooms: 0,
            pricePerRoom: 0,
            discount: 0
        }]);
    };

    function getRoomDetailsByCategory(category) {
        const rc = roomCategories.find(rc => rc.category === category);
        return rc;
    }

    const handleCategoryChange = (index, event) => {
        const category = event.target.value;
        const rc = getRoomDetailsByCategory(category);
        let pricePerRoom = rc.price + (rc.price * rc.gst / 100);

        const updatedRoomBookings = [...bookingFormik.values.roomBookings];

        updatedRoomBookings[index] = {
            ...updatedRoomBookings[index],
            pricePerRoom,
            category,
        };

        bookingFormik.setFieldValue("roomBookings", updatedRoomBookings);
    };

    let handleDeleteRoom = (index) => {
        const updatedRoomBookings = [...bookingFormik.values.roomBookings];
        updatedRoomBookings.splice(index, 1);
        bookingFormik.setFieldValue("roomBookings", updatedRoomBookings);
    };

    let handleProofView = async (documentS3Id) => {
        try {
            setIsLoading(true);
            const token = await fetchAccessToken();
            const imageUrl = await getImageUrl(documentS3Id, token);
            if (imageUrl) {
                setImageUrl(imageUrl);
                setShowProofImage(true);
            }
        } catch (ex) {
            alert("Error occurred while viewing the Proof.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Card className="h-full w-full">
                <CardHeader variant="gradient" color="gray" className="mt-3 mb-3 p-3 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        {isAdmin ? "Edit Booking" : "View Booking"}
                    </Typography>
                </CardHeader>
                <CardBody className="overflow-scroll px-3 pt-3 pb-2">
                    <div className="grid grid-cols-2 gap-2" >
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="guestMobile" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Guest Mobile  <span className="text-red-500">*</span></label>
                            <input type="tel" id="guestMobile" name="guestMobile" className="border-0 ml-2 mr-2 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.guestMobile} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} readOnly={true} disabled={true} />
                            {bookingFormik.touched.guestMobile && bookingFormik.errors.guestMobile ? (
                                <div className="text-red-500">{bookingFormik.errors.guestMobile}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="guestName" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Guest Name <span className="text-red-500">*</span></label>
                            <input type="text" id="guestName" name="guestName"
                                className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.guestName} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                            {bookingFormik.touched.guestName && bookingFormik.errors.guestName ? (
                                <div className="text-red-500">{bookingFormik.errors.guestName}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="bookingDate" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Booking Date <span className="text-red-500">*</span></label>
                            <input type="date" id="bookingDate" name="bookingDate" className="border-0 ml-2 mr-2 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.bookingDate} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                            {bookingFormik.touched.bookingDate && bookingFormik.errors.bookingDate ? (
                                <div className="text-red-500">{bookingFormik.errors.bookingDate}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="bookingTime" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Booking Time  <span className="text-red-500">*</span></label>
                            <input type="time" id="bookingTime" name="bookingTime" className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.bookingTime} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                            {bookingFormik.touched.bookingTime && bookingFormik.errors.bookingTime ? (
                                <div className="text-red-500">{bookingFormik.errors.bookingTime}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="bookingDays" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">No. of Days  <span className="text-red-500">*</span></label>
                            <select id="bookingDays" name="bookingDays" className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.bookingDays} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur}>
                                <option key="1" value={1}>1</option>
                                <option key="2" value={2}>2</option>
                                <option key="3" value={3}>3</option>
                                <option key="4" value={4}>4</option>
                                <option key="5" value={5}>5</option>
                                <option key="6" value={6}>6</option>
                                <option key="7" value={7}>7</option>
                            </select>
                            {bookingFormik.touched.bookingDays && bookingFormik.errors.bookingDays ? (
                                <div className="text-red-500">{bookingFormik.errors.bookingDays}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">

                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label className="uppercase text-blueGray-600 text-xs font-bold mb-2 flex flex-row" >
                                Room Details
                            </label>
                            {typeof bookingFormik.errors.roomBookings === 'string' && bookingFormik.errors.roomBookings !== undefined ? (
                                <div className="text-red-500">{bookingFormik.errors.roomBookings}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <IconButton color="green" onClick={handleAddRoom}>
                                <IoIosAddCircleOutline className="w-5 h-5" />
                            </IconButton>
                        </div>
                        {bookingFormik.values.roomBookings.map((roomBooking, index) =>
                        (<div key={index} className="mr-3 mt-3 col-span-2 flex flex-row flex-wrap border-2 border-blue-500 rounded-lg">
                            <div className="ml-3 w-50">
                                <label htmlFor={`roomBookings.${index}.category`} className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Category<span className="text-red-500">*</span></label>
                                <select label="Category" color="blue" className="bg-white text-gray-900 border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    name={`roomBookings.${index}.category`} id={`roomBookings.${index}.category`}
                                    onChange={(event) => handleCategoryChange(index, event)}
                                    onBlur={bookingFormik.handleBlur}
                                    value={roomBooking.category}>
                                    <option value="">Select</option>
                                    {roomCategories.map((category, i) => (
                                        <option key={i} value={category.category}>{category.category}</option>
                                    ))}
                                </select>
                                {bookingFormik.touched.roomBookings && bookingFormik.touched.roomBookings[index] && bookingFormik.errors.roomBookings && bookingFormik.errors.roomBookings[index] && bookingFormik.errors.roomBookings[index].category ? (
                                    <div className="text-red-500">{bookingFormik.errors.roomBookings[index].category}</div>
                                ) : null}
                            </div>
                            <div className="m-3 w-50">
                                <label htmlFor={`roomBookings.${index}.numberOfRooms`} className="block uppercase text-blueGray-600 text-xs font-bold mb-2">No.of Rooms<span className="text-red-500">*</span></label>
                                <input type="number" name={`roomBookings.${index}.numberOfRooms`} id={`roomBookings.${index}.numberOfRooms`}
                                    className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    value={roomBooking.numberOfRooms} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                                {bookingFormik.touched.roomBookings && bookingFormik.touched.roomBookings[index] && bookingFormik.errors.roomBookings && bookingFormik.errors.roomBookings[index] && bookingFormik.errors.roomBookings[index].numberOfRooms ? (
                                    <div className="text-red-500">{bookingFormik.errors.roomBookings[index].numberOfRooms}</div>
                                ) : null}
                            </div>
                            <div className="m-3 w-50">
                                <label htmlFor={`roomBookings.${index}.pricePerRoom`} className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Per Room Cost<span className="text-red-500">*</span></label>
                                <input type="number" name={`roomBookings.${index}.pricePerRoom`} id={`roomBookings.${index}.pricePerRoom`}
                                    className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    value={roomBooking.pricePerRoom} disabled={true} readOnly={true} />
                                {bookingFormik.touched.roomBookings && bookingFormik.touched.roomBookings[index] && bookingFormik.errors.roomBookings && bookingFormik.errors.roomBookings[index] && bookingFormik.errors.roomBookings[index].pricePerRoom ? (
                                    <div className="text-red-500">{bookingFormik.errors.roomBookings[index].pricePerRoom}</div>
                                ) : null}
                            </div>
                            <div className="m-3 w-50">
                                <label htmlFor={`roomBookings.${index}.discount`} className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Per Room Discount</label>
                                <input type="number" name={`roomBookings.${index}.discount`} id={`roomBookings.${index}.discount`}
                                    className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    value={roomBooking.discount} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                                {bookingFormik.touched.roomBookings && bookingFormik.touched.roomBookings[index] && bookingFormik.errors.roomBookings && bookingFormik.errors.roomBookings[index] && bookingFormik.errors.roomBookings[index].discount ? (
                                    <div className="text-red-500">{bookingFormik.errors.roomBookings[index].discount}</div>
                                ) : null}
                            </div>
                            <div className="ml-3 w-50">
                                {index !== 0 && <div>
                                    <IconButton color="red" className="mr-3 mt-9"
                                        onClick={() => handleDeleteRoom(index)}>
                                        <IoMdRemoveCircleOutline className="w-5 h-5" />
                                    </IconButton>
                                </div>
                                }
                            </div>
                        </div>
                        ))}
                        <div className="mr-3 mt-3 col-span-2">
                            <label className="uppercase text-blueGray-600 text-xs font-bold mb-2 flex flex-row" >
                                Price Details
                            </label>
                        </div>
                        <div className="mr-3 mt-3 col-span-2">
                            <table className="table-auto w-full">
                                <tbody>
                                    <tr>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                Total Cost
                                            </label>
                                        </td>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                {totalCost}.00
                                            </label>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                Total Discount
                                            </label>
                                        </td>
                                        <td> <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                            {totalDiscount}.00
                                        </label>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                Effective Cost
                                            </label>
                                        </td>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                {totalCost - totalDiscount}.00
                                            </label>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                Pending Amount
                                            </label>
                                        </td>
                                        <td>
                                            <label className="text-blueGray-600 text-md mb-2 flex flex-row" >
                                                {totalCost - totalDiscount - bookingFormik.values.advancePayment}.00
                                            </label>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="paymentMode" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Payment Type </label>
                            <select label="Payment Type" color="blue" className="bg-white text-gray-900 border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                name="paymentMode" id="paymentMode"
                                onChange={bookingFormik.handleChange}
                                onBlur={bookingFormik.handleBlur}
                                value={bookingFormik.values.paymentMode}>
                                <option key="" value="">Select</option>
                                <option key="Cash" value="Cash">Cash</option>
                                <option key="UPI" value="UPI">UPI</option>
                                <option key="Card" value="Card">Card</option>
                            </select>
                            {bookingFormik.touched.paymentMode && bookingFormik.errors.paymentMode ? (
                                <div className="text-red-500">{bookingFormik.errors.paymentMode}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="advancePayment" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Advance Amount</label>
                            <input type="number" id="advancePayment" name="advancePayment"
                                className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.advancePayment} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                            {bookingFormik.touched.advancePayment && bookingFormik.errors.advancePayment ? (
                                <div className="text-red-500">{bookingFormik.errors.advancePayment}</div>
                            ) : null}
                        </div>
                        <div className="w-50 mr-3 mt-3">
                            <label htmlFor="paymentDate" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Payment Date </label>
                            <input type="date" id="paymentDate" name="paymentDate" className="border-0 ml-2 mr-2 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                value={bookingFormik.values.paymentDate} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                            {bookingFormik.touched.paymentDate && bookingFormik.errors.paymentDate ? (
                                <div className="text-red-500">{bookingFormik.errors.paymentDate}</div>
                            ) : null}
                        </div>
                    </div>
                    <div className="w-50 mr-3 mt-3">
                        <label htmlFor="notes" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Remarks </label>
                        <input type="text" id="notes" name="notes"
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                            value={bookingFormik.values.notes} onChange={bookingFormik.handleChange} onBlur={bookingFormik.handleBlur} />
                        {bookingFormik.touched.notes && bookingFormik.errors.notes ? (
                            <div className="text-red-500">{bookingFormik.errors.notes}</div>
                        ) : null}
                    </div>
                    <div className="mr-3 mt-3 col-span-2">
                        <label htmlFor="proof" className="block uppercase text-blueGray-600 text-xs font-bold mb-2">Proof</label>
                        <div className="flex flex-col lg:w-4/12">
                            <div className="py-3">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={useCamera}
                                        onChange={(e) => setUseCamera(e.target.checked)}
                                    />
                                    Use Camera
                                </label>
                                <input type="file" accept="image/*" capture={useCamera ? "environment" : undefined} onChange={handleFileChange} />
                            </div>
                        </div>
                        <div className="relative w-full px-5">
                            {attachment &&
                                <div>
                                    <img src={URL.createObjectURL(attachment)} style={{ height: "100px", width: "100px", margin: "5px" }} />
                                </div>
                            }
                            <ul>
                                {bookingFormik.values.documentS3Ids.map((s3Id, i) => <li className="cursor-pointer"><u key={i} onClick={() => handleProofView(s3Id)}>{"Proof "} {i + 1}</u></li>)}
                            </ul>
                        </div>
                    </div>

                </CardBody>
                <CardFooter>
                    <div className="w-full flex justify-end  mb-0 px-6 py-6">
                        {isAdmin && bookingFormik.values.bookingStatus === "CONFIRMED" && <button className="bg-green-500 text-white hover:bg-green-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150" type="button" onClick={handleSubmitInternal}>
                            Update
                        </button>
                        }
                        {isAdmin && bookingFormik.values.bookingStatus === "CONFIRMED" && <button className="bg-pink-500 text-white hover:bg-pink-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150" type="button" onClick={deleteBookingInternal}>
                            Cancel
                        </button>
                        }
                        <button className="bg-blue-500 text-white hover:bg-blue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-2 ease-linear transition-all duration-150" type="button" onClick={(e) => navigate(-1)}>
                            Back
                        </button>
                    </div>
                </CardFooter>
            </Card>
            <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>
            <Dialog size={"xs"} open={isShowProofImage}>
                <DialogHeader className="flex justify-end items-center h-full">
                    <Button
                        variant="text"
                        color="red"
                        onClick={() => setShowProofImage(false)}
                        className="mr-1"
                    >
                        <span>Close</span>
                    </Button>
                </DialogHeader>
                <DialogBody>
                    <img src={imageUrl} />
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        color="red"
                        onClick={() => setShowProofImage(false)}
                        className="mr-1"
                    >
                        <span>Close</span>
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};
